import { useEffect } from 'react'
import {
  AutocompleteInput,
  ChipField,
  Datagrid,
  downloadCSV,
  ExportButton,
  FunctionField,
  List,
  ReferenceArrayField,
  ReferenceField,
  ReferenceInput,
  SingleFieldList,
  TabbedShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  useListContext,
  useListFilterContext,
} from 'react-admin'
import jsonExport from 'jsonexport/dist'

import FilterComponent from '../../components/Filter'
import { ROLES } from '../../components/constants'

// ============================================
// CONFIGURATION - Centralized tab settings
// ============================================
const CONTENT_TABS = [
  {
    key: 'books',
    label: 'Books',
    entity: 'books',
    fileName: 'Content Info - Audiobook',
    includeAuthor: true,
    includePublisher: true,
    includePrimaryCategory: true,
    primaryCategorySource: 'primaryCategoryId',
    primaryCategoryIsArray: false,
  },
  {
    key: 'courses',
    label: 'Courses',
    entity: 'courses',
    fileName: 'Content Info - PodCourse',
    includeAuthor: false,
    includePublisher: true,
    includePrimaryCategory: true,
    primaryCategorySource: 'primaryCategoryIds',
    primaryCategoryIsArray: true,
  },
  {
    key: 'english-books',
    label: 'English books',
    entity: 'english-books',
    fileName: 'Content Info - English books',
    includeAuthor: true,
    includePublisher: true,
    includePrimaryCategory: true,
    primaryCategorySource: 'primaryCategoryId',
    primaryCategoryIsArray: false,
  },
  {
    key: 'ebooks',
    label: 'Ebooks',
    entity: 'ebooks',
    fileName: 'Content Info - Ebooks',
    includeAuthor: true,
    includePublisher: false,
    includePrimaryCategory: true,
    primaryCategorySource: 'primaryCategoryId',
    primaryCategoryIsArray: false,
  },
]

const timeConversion = duration => {
  const portions = []

  const msInHour = 60 * 60
  const hours = Math.trunc(duration / msInHour)
  if (hours > 0) {
    portions.push(hours > 9 ? hours : `0${hours}`)
    duration = duration - hours * msInHour
  } else {
    portions.push('00')
  }

  const msInMinute = 60
  const minutes = Math.trunc(duration / msInMinute)
  if (minutes > 0) {
    portions.push(minutes > 9 ? minutes : `0${minutes}`)
    duration = duration - minutes * msInMinute
  } else {
    portions.push('00')
  }

  const seconds = Math.trunc(duration)
  if (seconds > 0) {
    portions.push(seconds > 9 ? seconds : `0${seconds}`)
  } else {
    portions.push('00')
  }

  return portions.join(':')
}

const ContentInfoActions = ({
  resource,
  currentSort,
  filterValues,
  exporter,
}) => (
  <TopToolbar>
    <ExportButton
      disabled={false}
      resource={resource}
      sort={currentSort}
      filter={filterValues}
      exporter={exporter}
      maxResults={10000}
    />
  </TopToolbar>
)

// ============================================
// FILTERS
// ============================================
const ContentInfoFilter = ({
  includeAuthor,
  includePublisher,
  includePrimaryCategory,
  primaryCategorySource = 'primaryCategoryId',
}) => (
  <FilterComponent>
    <TextInput label="Search by title" source="title" alwaysOn />

    {includePublisher && (
      <ReferenceInput
        label="Publisher"
        source="publisherId"
        reference="users"
        filter={{ role: 'publisher' }}
        alwaysOn>
        <AutocompleteInput
          optionText="name"
          filterToQuery={searchText => ({
            name: { $iLike: `%${searchText}%` },
            role: ROLES.PUBLISHER,
          })}
        />
      </ReferenceInput>
    )}

    {includeAuthor && (
      <ReferenceInput
        label="Author"
        source="authorId"
        reference="authors"
        alwaysOn>
        <AutocompleteInput
          optionText="name"
          filterToQuery={searchText => ({
            name: { $iLike: `%${searchText}%` },
          })}
        />
      </ReferenceInput>
    )}

    {includePrimaryCategory && (
      <ReferenceInput
        label="Primary Category"
        source={primaryCategorySource}
        reference="categories"
        alwaysOn>
        <AutocompleteInput
          optionText="name"
          filterToQuery={searchText => ({
            name: { $iLike: `%${searchText}%` },
          })}
        />
      </ReferenceInput>
    )}
  </FilterComponent>
)

// ============================================
// EXPORTER
// ============================================
const createContentInfoExporter =
  (
    fileName,
    includeAuthor,
    includePublisher,
    includePrimaryCategory,
    primaryCategorySource = 'primaryCategoryId',
    primaryCategoryIsArray = false,
  ) =>
  async (records, fetchRelatedRecords) => {
    const promises = []
    let categoryIndex = -1
    let authorIndex = -1
    let publisherIndex = -1

    // Fetch categories
    if (includePrimaryCategory && !primaryCategoryIsArray) {
      categoryIndex = promises.length
      promises.push(
        fetchRelatedRecords(records, primaryCategorySource, 'categories'),
      )
    }

    // Fetch authors
    if (includeAuthor) {
      authorIndex = promises.length
      promises.push(fetchRelatedRecords(records, 'authorId', 'authors'))
    }

    // Fetch publishers
    if (includePublisher) {
      publisherIndex = promises.length
      promises.push(fetchRelatedRecords(records, 'publisherId', 'users'))
    }

    const results = await Promise.all(promises)

    // Process categories
    let categories = {}
    if (includePrimaryCategory && !primaryCategoryIsArray) {
      categories = results[categoryIndex] || {}
    } else if (includePrimaryCategory && primaryCategoryIsArray) {
      const uniqueIds = [
        ...new Set(
          records.flatMap(record =>
            Array.isArray(record?.[primaryCategorySource])
              ? record[primaryCategorySource]
              : [],
          ),
        ),
      ]
      if (uniqueIds.length) {
        const dummyRecords = uniqueIds.map(id => ({ __categoryId: id }))
        const fetched = await fetchRelatedRecords(
          dummyRecords,
          '__categoryId',
          'categories',
        )
        categories = fetched || {}
      }
    }

    const authors = includeAuthor ? results[authorIndex] : {}
    const publishers = includePublisher ? results[publisherIndex] : {}

    // Build headers
    const headers = ['Id', 'Title', 'Duration']
    if (includeAuthor) headers.push('Author')
    if (includePublisher) headers.push('Publisher')
    if (includePrimaryCategory) headers.push('Primary category')

    // Build data
    const data = records.map(record => {
      const row = {
        Id: record.id,
        Title: record.title,
        Duration: timeConversion(record.duration || 0),
      }

      if (includeAuthor) {
        row.Author = authors?.[record.authorId]?.name || ''
      }

      if (includePublisher) {
        row.Publisher = publishers?.[record.publisherId]?.name || ''
      }

      if (includePrimaryCategory) {
        const rawValue = record[primaryCategorySource]
        if (primaryCategoryIsArray) {
          const names = Array.isArray(rawValue)
            ? rawValue.map(id => categories?.[id]?.name || id).filter(Boolean)
            : []
          row['Primary category'] = names.join(', ')
        } else {
          row['Primary category'] = categories?.[rawValue]?.name || ''
        }
      }

      return row
    })

    jsonExport(data, { headers }, (err, csv) => {
      if (err) return
      downloadCSV(csv, fileName)
    })
  }

// ============================================
// DATAGRID COLUMNS
// ============================================
const PrimaryCategoryColumn = ({
  source = 'primaryCategoryId',
  isArray = false,
}) =>
  isArray ? (
    <ReferenceArrayField
      source={source}
      reference="categories"
      label="Primary category"
      sortBy="primaryCategoryIds">
      <SingleFieldList linkType="show">
        <ChipField source="name" />
      </SingleFieldList>
    </ReferenceArrayField>
  ) : (
    <ReferenceField
      source={source}
      reference="categories"
      label="Primary category"
      sortBy="primaryCategoryId">
      <TextField source="name" />
    </ReferenceField>
  )

const ContentInfoDatagrid = ({
  includeAuthor,
  includePublisher,
  includePrimaryCategory = true,
  primaryCategorySource = 'primaryCategoryId',
  primaryCategoryIsArray = false,
}) => (
  <Datagrid rowClick={false} bulkActionButtons={false}>
    <TextField source="id" />
    <TextField source="title" />
    <FunctionField
      render={record => `${timeConversion(record.duration || 0)}`}
      source="duration"
      label="Duration"
    />

    {includeAuthor && (
      <ReferenceField source="authorId" reference="authors" label="Author">
        <TextField source="name" />
      </ReferenceField>
    )}

    {includePublisher && (
      <ReferenceField source="publisherId" reference="users" label="Publisher">
        <TextField source="name" />
      </ReferenceField>
    )}

    {includePrimaryCategory && (
      <PrimaryCategoryColumn
        source={primaryCategorySource}
        isArray={primaryCategoryIsArray}
      />
    )}
  </Datagrid>
)

// ============================================
// FILTER CLEANER
// ============================================
const FilterCleaner = ({
  includeAuthor,
  includePublisher,
  includePrimaryCategory,
  primaryCategorySource = 'primaryCategoryId',
}) => {
  const { filterValues, setFilters } = useListFilterContext()
  const { sort, setSort } = useListContext()

  useEffect(() => {
    if (!filterValues) return

    let hasChanges = false
    const newFilters = { ...filterValues }

    // Clean up authorId if not included
    if (!includeAuthor && newFilters.authorId !== undefined) {
      delete newFilters.authorId
      hasChanges = true
    }

    // Clean up publisherId if not included
    if (!includePublisher && newFilters.publisherId !== undefined) {
      delete newFilters.publisherId
      hasChanges = true
    }

    // Clean up category filters for both single and array cases
    if (!includePrimaryCategory) {
      if (newFilters.primaryCategoryId !== undefined) {
        delete newFilters.primaryCategoryId
        hasChanges = true
      }
      if (newFilters.primaryCategoryIds !== undefined) {
        delete newFilters.primaryCategoryIds
        hasChanges = true
      }
    } else {
      // When switching tabs, clean up the wrong category source
      const otherSource =
        primaryCategorySource === 'primaryCategoryId'
          ? 'primaryCategoryIds'
          : 'primaryCategoryId'

      if (newFilters[otherSource] !== undefined) {
        delete newFilters[otherSource]
        hasChanges = true
      }
    }

    if (hasChanges) {
      setFilters(newFilters)
    }
  }, [
    filterValues,
    includeAuthor,
    includePublisher,
    includePrimaryCategory,
    primaryCategorySource,
    setFilters,
  ])

  useEffect(() => {
    const sortField = sort?.field
    if (
      !includePrimaryCategory &&
      (sortField === 'primaryCategoryId' || sortField === 'primaryCategoryIds')
    ) {
      setSort({ field: 'updatedAt', order: 'DESC' })
    }
  }, [sort, includePrimaryCategory, setSort])

  return null
}

// ============================================
// DEBUG COMPONENT
// ============================================
const ListDebugger = ({ label }) => {
  const { data, isLoading, total, filterValues } = useListContext()

  useEffect(() => {
    if (!label) return
    const records = data ? Object.values(data) : []
    console.log('[ContentInfo] Debug', label, {
      records,
      total,
      isLoading,
      filterValues,
    })
  }, [label, data, isLoading, total, filterValues])

  return null
}

// ============================================
// ENTITY LIST COMPONENT
// ============================================
const ContentInfoEntityList = ({
  entity,
  fileName,
  includeAuthor,
  includePublisher,
  includePrimaryCategory = true,
  primaryCategorySource = 'primaryCategoryId',
  primaryCategoryIsArray = false,
  debugLabel,
  ...props
}) => {
  const filterDefaultValues = {}
  if (!includeAuthor) filterDefaultValues.authorId = null
  if (!includePublisher) filterDefaultValues.publisherId = null

  return (
    <List
      {...props}
      resource={entity}
      filterDefaultValues={filterDefaultValues}
      filters={
        <>
          <FilterCleaner
            includeAuthor={includeAuthor}
            includePublisher={includePublisher}
            includePrimaryCategory={includePrimaryCategory}
            primaryCategorySource={primaryCategorySource}
          />
          <ContentInfoFilter
            includeAuthor={includeAuthor}
            includePublisher={includePublisher}
            includePrimaryCategory={includePrimaryCategory}
            primaryCategorySource={primaryCategorySource}
          />
        </>
      }
      actions={<ContentInfoActions />}
      exporter={createContentInfoExporter(
        fileName,
        includeAuthor,
        includePublisher,
        includePrimaryCategory,
        primaryCategorySource,
        primaryCategoryIsArray,
      )}
      sort={{ field: 'updatedAt', order: 'DESC' }}>
      {debugLabel && <ListDebugger label={debugLabel} />}
      <ContentInfoDatagrid
        includeAuthor={includeAuthor}
        includePublisher={includePublisher}
        includePrimaryCategory={includePrimaryCategory}
        primaryCategorySource={primaryCategorySource}
        primaryCategoryIsArray={primaryCategoryIsArray}
      />
    </List>
  )
}

// ============================================
// MAIN COMPONENT
// ============================================
const ContentInfoList = () => (
  <TabbedShowLayout record={{ id: 1 }}>
    {CONTENT_TABS.map(tab => (
      <TabbedShowLayout.Tab key={tab.key} label={tab.label}>
        <ContentInfoEntityList
          entity={tab.entity}
          fileName={tab.fileName}
          includeAuthor={tab.includeAuthor}
          includePublisher={tab.includePublisher}
          includePrimaryCategory={tab.includePrimaryCategory}
          primaryCategorySource={tab.primaryCategorySource}
          primaryCategoryIsArray={tab.primaryCategoryIsArray}
          debugLabel={tab.key}
        />
      </TabbedShowLayout.Tab>
    ))}
  </TabbedShowLayout>
)

export default ContentInfoList
